/**
 * Bigens Subscription Selector V2
 * Versão otimizada - não substitui a v1, coexiste com ela
 * Versão: 2.0.0
 */

class BigensSubscriptionSelectorV2 {
  constructor(config = {}) {
    this.config = {
      containerSelector: config.containerSelector || '#bigens-subscription-selector-v2',
      variantPickerSelector: config.variantPickerSelector || 'variant-main-picker, variant-radios, variant-selects',
      productFormSelector: config.productFormSelector || 'product-form',
      discountBadge: config.discountBadge || '10% OFF',
      subscriptionMap: config.subscriptionMap || {
        'mensal': 30,
        '30': 30,
        '30 dias': 30,
        '1 mês': 30,
        'monthly': 30,
        'bimestral': 60,
        '60': 60,
        '60 dias': 60,
        '2 meses': 60,
        'bimonthly': 60,
        'trimestral': 90,
        '90': 90,
        '90 dias': 90,
        '3 meses': 90,
        'quarterly': 90
      },
      // Termos de assinatura para o modal
      termsContent: config.termsContent || null
    };

    this.state = {
      selectedType: 'one-time', // 'one-time' ou 'subscription'
      selectedPeriod: 30,
      productData: null,
      variants: {
        oneTime: null,
        subscriptions: []
      },
      modalOpen: false,
      pendingAddToCart: false,
      // Preços lidos do Kaching Bundle (v2.1)
      currentPrice: null,
      originalPrice: null
    };

    this.init();
  }

  init() {
    this.loadProductData();
    this.identifyVariants();
    this.render();
    this.attachEventListeners();
    this.observeVariantChanges();
    this.interceptAddToCart();
    this.observeKachingPrices(); // v2.1: sincroniza preço com Kaching Bundle
  }

  loadProductData() {
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
      this.state.productData = window.ShopifyAnalytics.meta.product;
    }

    const productJsonElement = document.querySelector('[data-product-json]');
    if (productJsonElement) {
      try {
        this.state.productData = JSON.parse(productJsonElement.textContent);
      } catch (e) {
        console.error('Erro ao carregar dados do produto:', e);
      }
    }

    if (!this.state.productData) {
      const productElement = document.querySelector('[data-product-id]');
      if (productElement) {
        this.fetchProductData(productElement.dataset.productId);
      }
    }
  }

  async fetchProductData(productId) {
    try {
      const slug = window.location.pathname.split('/products/')[1];
      const response = await fetch(`/products/${slug}.js`);
      this.state.productData = await response.json();
      this.identifyVariants();
      this.render();
      this.attachEventListeners();
    } catch (e) {
      console.error('Erro ao buscar dados do produto:', e);
    }
  }

  identifyVariants() {
    if (!this.state.productData || !this.state.productData.variants) return;

    const variants = this.state.productData.variants;
    const periodsFound = {};

    variants.forEach(variant => {
      const title = variant.title.toLowerCase();
      const option1 = variant.option1?.toLowerCase() || '';
      const option2 = variant.option2?.toLowerCase() || '';
      const option3 = variant.option3?.toLowerCase() || '';
      const allOptions = `${title} ${option1} ${option2} ${option3}`;

      let isSubscription = false;
      let period = null;

      Object.keys(this.config.subscriptionMap).forEach(key => {
        if (allOptions.includes(key)) {
          isSubscription = true;
          period = this.config.subscriptionMap[key];
        }
      });

      if (isSubscription && period) {
        if (!periodsFound[period]) {
          this.state.variants.subscriptions.push({ ...variant, period });
          periodsFound[period] = true;
        }
      } else if (
        allOptions.includes('única') ||
        allOptions.includes('compra única') ||
        (!isSubscription && this.state.variants.subscriptions.length === 0)
      ) {
        if (!this.state.variants.oneTime) {
          this.state.variants.oneTime = variant;
        }
      }
    });

    this.state.variants.subscriptions.sort((a, b) => a.period - b.period);

    if (!this.state.variants.oneTime && this.state.variants.subscriptions.length > 0) {
      const nonSubVariants = variants.filter(
        v => !this.state.variants.subscriptions.find(s => s.id === v.id)
      );
      if (nonSubVariants.length > 0) {
        this.state.variants.oneTime = nonSubVariants[0];
      }
    }
  }

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  render() {
    const container = document.querySelector(this.config.containerSelector);
    if (!container) {
      console.warn('Container v2 não encontrado:', this.config.containerSelector);
      return;
    }

    container.innerHTML = `
      <div class="bsv2-wrapper">
        ${this.renderSubscriptionToggle()}
        ${this.renderModal()}
      </div>
    `;

    this.updateAddToCartButton();
    // Atualiza preço após cada render (garante exibição mesmo sem assinatura selecionada)
    this._readKachingPrices();
  }

  renderSubscriptionToggle() {
    const isSelected = this.state.selectedType === 'subscription';

    return `
      <div class="bsv2-card ${isSelected ? 'bsv2-card--active' : ''}" data-bsv2-subscription-card>
        
        <div class="bsv2-card__header" data-bsv2-toggle>
          <div class="bsv2-toggle ${isSelected ? 'bsv2-toggle--on' : ''}">
            <div class="bsv2-toggle__knob"></div>
          </div>
          <div class="bsv2-card__info">
            <span class="bsv2-label">Assine e economize 🧡</span>
            <span class="bsv2-sublabel">Receba recorrente e cancele ou altere quando quiser.</span>
          </div>
          <div class="bsv2-price-badge-col">
            ${this._renderPriceStack()}
            <div class="bsv2-badge ${isSelected ? 'bsv2-badge--active' : ''}" data-bsv2-discount-badge>
              ${this.config.discountBadge}
            </div>
          </div>
        </div>

        ${isSelected ? this.renderPeriodSelector() : ''}
      </div>
    `;
  }

  renderPeriodSelector() {
    if (this.state.variants.subscriptions.length === 0) return '';

    const uniquePeriods = {};
    this.state.variants.subscriptions.forEach(v => {
      if (!uniquePeriods[v.period]) uniquePeriods[v.period] = v;
    });

    const periodsHtml = Object.values(uniquePeriods).map(variant => {
      const isSelected = this.state.selectedPeriod === variant.period;
      return `
        <button
          class="bsv2-chip ${isSelected ? 'bsv2-chip--active' : ''}"
          data-bsv2-period="${variant.period}"
          data-bsv2-variant-id="${variant.id}"
          type="button"
        >
          ${variant.period} dias
        </button>
      `;
    }).join('');

    return `
      <div class="bsv2-period-section">
        <span class="bsv2-period-label">Receber a cada:</span>
        <div class="bsv2-chips">
          ${periodsHtml}
        </div>
      </div>
      ${this.renderShipmentSimulator()}
    `;
  }

  // ─── SHIPMENT SIMULATOR ──────────────────────────────────────────────────
  // Classe raiz: bsv2-shipment-simulator
  // Use essa classe no Intelligems para ocultar o bloco no teste A/B:
  //   Variante B (sem simulador): .bsv2-shipment-simulator { display: none !important; }

  calculateShipmentDates(period, count = 3) {
    const dates = [];
    const start = new Date();
    for (let i = 0; i < count; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + period * i);
      dates.push(d);
    }
    return dates;
  }

  formatDate(date) {
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  renderShipmentSimulator() {
    const dates = this.calculateShipmentDates(this.state.selectedPeriod, 3);

    const shipmentsHtml = dates.map((date, i) => `
      <div class="bsv2-shipment__item">
        <div class="bsv2-shipment__label">${i + 1}° ENVIO</div>
        <div class="bsv2-shipment__date">${this.formatDate(date)}</div>
        <div class="bsv2-shipment__dot-line">
          <div class="bsv2-shipment__dot"></div>
          <div class="bsv2-shipment__line"></div>
        </div>
      </div>
    `).join('');

    return `
      <div class="bsv2-shipment-simulator">
        <div class="bsv2-shipment__title">Simulação de envios*</div>
        <div class="bsv2-shipment__grid">
          ${shipmentsHtml}
        </div>
        <div class="bsv2-shipment__note">
          *As datas referem-se à renovação do pedido, não ao prazo de entrega.
        </div>
      </div>
    `;
  }

  renderModal() {
    return `
      <div class="bsv2-modal-overlay ${this.state.modalOpen ? 'bsv2-modal-overlay--open' : ''}" data-bsv2-modal-overlay>
        <div class="bsv2-modal" role="dialog" aria-modal="true" aria-labelledby="bsv2-modal-title">
          <div class="bsv2-modal__header">
            <h2 id="bsv2-modal-title" class="bsv2-modal__title">Termos da Assinatura</h2>
            <button class="bsv2-modal__close" data-bsv2-modal-close aria-label="Fechar" type="button">✕</button>
          </div>
          <div class="bsv2-modal__body">
            ${this.config.termsContent || this.getDefaultTerms()}
          </div>
          <div class="bsv2-modal__footer">
            <button class="bsv2-modal__btn bsv2-modal__btn--cancel" data-bsv2-modal-close type="button">
              Cancelar
            </button>
            <button class="bsv2-modal__btn bsv2-modal__btn--confirm" data-bsv2-modal-confirm type="button">
              Aceitar e assinar
            </button>
          </div>
        </div>
      </div>
    `;
  }

  getDefaultTerms() {
    const periodLabel = {
      30: 'mensal (a cada 30 dias)',
      60: 'bimestral (a cada 60 dias)',
      90: 'trimestral (a cada 90 dias)'
    }[this.state.selectedPeriod] || `a cada ${this.state.selectedPeriod} dias`;

    return `
      <div class="bsv2-terms">
        <p>Ao confirmar sua assinatura, você está de acordo com o seguinte:</p>
        <ul>
          <li><strong>Desconto garantido:</strong> O desconto da assinatura vale em todas as renovações enquanto sua assinatura estiver ativa.</li>
          <li><strong>Cancelamento sem burocracia:</strong> Você pode cancelar ou alterar sua assinatura a qualquer momento, sem multa. É só acessar o portal do cliente ou falar com nosso suporte.</li>
          <li><strong>Entrega recorrente:</strong> Seus pedidos são processados e enviados conforme a frequência que você escolheu. O prazo de entrega começa a contar após o processamento.</li>
          <li><strong>Quer mudar a frequência?</strong> É só solicitar antes da próxima renovação.</li>
          <li><strong>Pagamento fácil e automático:</strong> A cobrança é feita direto no cartão ou método de pagamento cadastrado na frequência selecionada. Se houver algum problema, a gente te avisa para atualizar os dados.</li>
        </ul>
        <p class="bsv2-terms__note">Ficou com alguma dúvida? <a href="https://api.whatsapp.com/send?phone=5511939422905" target="_blank">Fale com a gente pelo WhatsApp.</a></p>
      </div>
    `;
  }

  // ─── KACHING PRICE SYNC (v2.1) ─────────────────────────────────────────

  /**
   * Lê os preços do bundle selecionado no Kaching.
   * Estrutura do Kaching:
   *   .kaching-bundles__bar--selected .kaching-bundles__bar-price       → preço com desconto
   *   .kaching-bundles__bar--selected .kaching-bundles__bar-full-price  → preço cheio (só quando há desconto de bundle)
   */
  /**
   * Atualiza os preços exibidos no badge da assinatura.
   *
   * Fonte: variantes de assinatura do Shopify — 100% dinâmico.
   *   "de"  → compare_at_price da variante de assinatura (preço cheio, sem desconto)
   *   "por" → price da variante de assinatura (preço com desconto cadastrado no admin)
   *
   * Regra de qual variante usar:
   *   - Assinatura selecionada: usa o período ativo (chip selecionado)
   *   - Assinatura não selecionada: usa o primeiro período disponível (padrão 30 dias)
   *
   * Quantidade do bundle NÃO entra no cálculo — preço exibido é por unidade.
   */
  /**
   * Calcula os preços "de" e "por" exibidos no badge.
   *
   * "de" (riscado) → preço cheio sem nenhum desconto = preço do Kaching ÷ (1 - descountRate)
   *                  Garante que o riscado sempre representa o preço da compra única equivalente.
   *
   * "por" (laranja):
   *   - Toggle DESLIGADO → preço do Kaching (já tem desconto de bundle, sem assinatura)
   *   - Toggle LIGADO    → preço do Kaching (já com desconto de bundle aplicado pelo Kaching)
   *                        O preço exibido pelo Kaching quando assinatura está ativa já é o correto.
   *
   * Resultado: "de" sempre mostra o preço cheio, "por" sempre mostra o preço com desconto ativo.
   */
  _readKachingPrices() {
    const selectedBar = document.querySelector('.kaching-bundles__bar--selected');
    if (!selectedBar) return;

    const kachingPriceEl = selectedBar.querySelector('.kaching-bundles__bar-price');
    if (!kachingPriceEl) return;

    const kachingPrice = this._parseBRL(kachingPriceEl.textContent.trim());
    if (!kachingPrice) return;

    const discountRate = this._getSubscriptionDiscountRate();

    let originalPrice, currentPrice;

    if (this.state.selectedType === 'subscription') {
      // Toggle LIGADO: "por" = preço do Kaching (correto), "de" = preço cheio (÷ desconto)
      currentPrice  = this._formatBRL(kachingPrice);
      originalPrice = this._formatBRL(kachingPrice / (1 - discountRate));
    } else {
      // Toggle DESLIGADO: "por" = Kaching × (1 - desconto), "de" = preço do Kaching sem desconto
      currentPrice  = this._formatBRL(kachingPrice * (1 - discountRate));
      originalPrice = this._formatBRL(kachingPrice);
    }

    const changed =
      this.state.currentPrice  !== currentPrice ||
      this.state.originalPrice !== originalPrice;

    if (changed) {
      this.state.currentPrice  = currentPrice;
      this.state.originalPrice = originalPrice;
      this._updatePriceStackDOM();
    }
  }


  /**
   * Calcula dinamicamente o percentual de desconto da assinatura
   * comparando variante one-time vs variante assinatura (1 unidade).
   * Evita hardcode do 10% — se mudar o desconto no admin, reflete aqui.
   * Fallback: 0.10 (10%)
   */
  _getSubscriptionDiscountRate() {
    const oneTime = this.state.variants.oneTime;
    const subs    = this.state.variants.subscriptions;
    if (!oneTime || !subs || subs.length === 0) return 0.10;

    const subVariant = subs.find(v => v.period === this.state.selectedPeriod) || subs[0];
    if (!subVariant || !subVariant.price || !oneTime.price) return 0.10;

    // desconto = 1 - (preço assinatura / preço compra única)
    const rate = 1 - (subVariant.price / oneTime.price);
    // Sanity check: desconto entre 1% e 50%
    return (rate > 0.01 && rate < 0.50) ? rate : 0.10;
  }

  /** Lê a quantidade selecionada no Kaching Bundle (1, 2, 3...) */
  _getKachingQty() {
    const selectedBar = document.querySelector('.kaching-bundles__bar--selected');
    if (!selectedBar) return 1;
    const titleEl = selectedBar.querySelector('.kaching-bundles__bar-title');
    if (!titleEl) return 1;
    const qty = parseInt(titleEl.textContent.trim());
    return isNaN(qty) ? 1 : qty;
  }

  /** Converte string BRL ("R$ 49,80") para número (49.80) */
  _parseBRL(str) {
    if (!str) return null;
    const num = parseFloat(
      str.replace(/[R$\s]/g, '').replace('.', '').replace(',', '.')
    );
    return isNaN(num) ? null : num;
  }

  /** Formata centavos para moeda BRL */
  _formatBRL(value) {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  /**
   * Atualiza só o bloco de preço no DOM sem re-renderizar o componente inteiro.
   * Isso evita flickering e perda de estado ao mudar bundle.
   */
  _updatePriceStackDOM() {
    const container = document.querySelector(this.config.containerSelector);
    if (!container) return;
    const stack = container.querySelector('[data-bsv2-price-stack]');
    if (!stack) return;
    stack.innerHTML = this._priceStackInner();
  }

  _renderPriceStack() {
    return `<div data-bsv2-price-stack>${this._priceStackInner()}</div>`;
  }

  _priceStackInner() {
    if (!this.state.currentPrice) return '';

    // originalPrice: preço cheio (riscado)
    // Pode vir do Kaching bar-full-price (desconto de bundle) ou
    // do preço sem assinatura calculado pela variante
    const original = this.state.originalPrice || this.state.variantFullPrice;

    if (original) {
      return `
        <div class="bsv2-price-stack">
          <span class="bsv2-price-original"><s>${original}</s></span>
          <span class="bsv2-price-current">${this.state.currentPrice}</span>
        </div>
      `;
    }

    // Fallback: só preço atual
    return `
      <div class="bsv2-price-stack">
        <span class="bsv2-price-current">${this.state.currentPrice}</span>
      </div>
    `;
  }

  observeKachingPrices() {
    // Leitura inicial com retry (Kaching pode demorar a montar)
    const tryRead = (attempts = 0) => {
      this._readKachingPrices();
      if (!this.state.currentPrice && attempts < 20) {
        setTimeout(() => tryRead(attempts + 1), 200);
      }
    };
    tryRead();

    // MutationObserver na div de bars do Kaching
    // Quando o usuário troca de bundle, a classe --selected migra entre os bars
    const observeKaching = () => {
      const barsContainer = document.querySelector('.kaching-bundles__bars');
      if (!barsContainer) {
        // Kaching ainda não montou — tenta de novo
        setTimeout(observeKaching, 300);
        return;
      }

      const observer = new MutationObserver(() => {
        this._readKachingPrices();
      });

      observer.observe(barsContainer, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
        childList: true,
        characterData: true
      });
    };

    observeKaching();

    // Fallback: escuta cliques nos bars do Kaching diretamente
    document.addEventListener('click', (e) => {
      const bar = e.target.closest('.kaching-bundles__bar');
      if (bar) {
        // Pequeno delay para o Kaching atualizar a classe --selected
        setTimeout(() => this._readKachingPrices(), 100);
        setTimeout(() => this._readKachingPrices(), 300);
      }
    });

    // Escuta evento customizado do Kaching se disponível
    document.addEventListener('kaching:bundle-selected', () => {
      setTimeout(() => this._readKachingPrices(), 100);
    });
  }

  // ─── BUTTON LABEL ─────────────────────────────────────────────────────────

  /**
   * Seleciona o span interno de texto do botão add-to-cart do tema Horizon.
   * Estrutura do Horizon:
   *   button[name="add"]
   *     span.add-to-cart-text
   *       span.add-to-cart-text__content
   *         span
   *           span  <-- texto aqui
   */
  _getAddToCartTextSpan() {
    // Tenta o span mais interno do Horizon primeiro
    const horizonSpan = document.querySelector(
      'button[name="add"] .add-to-cart-text__content span span,' +
      'button[name="add"] .add-to-cart-text__content span,' +
      'button[name="add"] .add-to-cart-text__content'
    );
    if (horizonSpan) return horizonSpan;

    // Fallback: outros temas Shopify
    return document.querySelector(
      '.product-form__submit .btn-text,' +
      'button[name="add"] .btn__text,' +
      'button[name="add"]'
    );
  }

  updateAddToCartButton() {
    const isSubscription = this.state.selectedType === 'subscription';
    const textEl = this._getAddToCartTextSpan();

    if (!textEl) return;

    // Guarda o texto original na primeira vez
    if (!textEl.dataset.bsv2OriginalLabel) {
      textEl.dataset.bsv2OriginalLabel = textEl.textContent.trim();
    }

    textEl.textContent = isSubscription
      ? 'Assinar produto'
      : textEl.dataset.bsv2OriginalLabel;
  }

  // ─── INTERCEPTAR ADD TO CART ──────────────────────────────────────────────

  interceptAddToCart() {
    document.addEventListener('click', (e) => {
      if (this.state.selectedType !== 'subscription') return;

      // Botão nativo do Horizon: button[name="add"] dentro de product-form-component ou form.shopify-product-form
      const btn = e.target.closest('button[name="add"]');
      if (!btn) return;

      // Garante que é o botão de add-to-cart e não outro botão da página
      const isAddToCart =
        btn.closest('product-form-component') ||
        btn.closest('form[action="/cart/add"]') ||
        btn.closest('.shopify-product-form') ||
        btn.dataset.testid === 'standalone-add-to-cart';

      if (!isAddToCart) return;

      // Evita loop: se já confirmado, deixa passar
      if (btn.dataset.bsv2Confirmed === 'true') {
        delete btn.dataset.bsv2Confirmed;
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      this.state.pendingBtn = btn;
      this.openModal();
    }, true); // useCapture = true — captura antes do handler do Shopify/Horizon
  }

  // ─── MODAL ────────────────────────────────────────────────────────────────

  openModal() {
    this.state.modalOpen = true;
    this.render();
    this.attachEventListeners();
    document.body.style.overflow = 'hidden';
    // Focus trap inicial
    setTimeout(() => {
      const confirmBtn = document.querySelector('[data-bsv2-modal-confirm]');
      if (confirmBtn) confirmBtn.focus();
    }, 50);
  }

  closeModal() {
    this.state.modalOpen = false;
    this.state.pendingBtn = null;
    this.render();
    this.attachEventListeners();
    document.body.style.overflow = '';
  }

  confirmSubscription() {
    // Salva referencia ANTES do closeModal zerar o state.pendingBtn
    const pendingBtn = this.state.pendingBtn;
    this.closeModal();

    const btn = pendingBtn || document.querySelector(
      'button[name="add"][data-testid="standalone-add-to-cart"],' +
      'button[name="add"]'
    );

    if (btn) {
      btn.dataset.bsv2Confirmed = 'true';
      btn.click();
    }
  }

  // ─── EVENTS ───────────────────────────────────────────────────────────────

  attachEventListeners() {
    const container = document.querySelector(this.config.containerSelector);
    if (!container) return;

    // Toggle de assinatura
    const toggleArea = container.querySelector('[data-bsv2-toggle]');
    if (toggleArea) {
      toggleArea.addEventListener('click', () => this.toggleSubscription());
    }

    // Chips de período
    container.querySelectorAll('[data-bsv2-period]').forEach(chip => {
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        const period = parseInt(chip.dataset.bsv2Period);
        const variantId = chip.dataset.bsv2VariantId;
        this.selectPeriod(period, variantId);
      });
    });

    // Fechar modal
    container.querySelectorAll('[data-bsv2-modal-close]').forEach(el => {
      el.addEventListener('click', () => this.closeModal());
    });

    // Overlay clique fora
    const overlay = container.querySelector('[data-bsv2-modal-overlay]');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) this.closeModal();
      });
    }

    // Confirmar assinatura
    const confirmBtn = container.querySelector('[data-bsv2-modal-confirm]');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => this.confirmSubscription());
    }

    // ESC fecha o modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state.modalOpen) this.closeModal();
    });
  }

  // ─── ACTIONS ──────────────────────────────────────────────────────────────

  toggleSubscription() {
    if (this.state.selectedType === 'subscription') {
      this.selectOneTime();
    } else {
      this.selectSubscription();
    }
  }

  selectOneTime() {
    this.state.selectedType = 'one-time';
    this.render();
    this.attachEventListeners();
    this.updateShopifyVariant(this.state.variants.oneTime?.id);
    this.updateAddToCartButton();
  }

  selectSubscription() {
    this.state.selectedType = 'subscription';
    if (!this.state.selectedPeriod && this.state.variants.subscriptions.length > 0) {
      this.state.selectedPeriod = this.state.variants.subscriptions[0].period;
    }
    this.render();
    this.attachEventListeners();

    const selectedVariant = this.state.variants.subscriptions.find(
      v => v.period === this.state.selectedPeriod
    );
    if (selectedVariant) {
      this.updateShopifyVariant(selectedVariant.id);
    }
    this.updateAddToCartButton();
  }

  selectPeriod(period, variantId) {
    this.state.selectedPeriod = period;
    this.render();
    this.attachEventListeners();
    this.updateShopifyVariant(variantId);
  }

  // ─── SHOPIFY INTEGRATION ─────────────────────────────────────────────────

  updateShopifyVariant(variantId) {
    if (!variantId) return;

    const variantPicker = document.querySelector(this.config.variantPickerSelector);
    if (variantPicker) {
      const radioInput = variantPicker.querySelector(`input[value="${variantId}"]`);
      if (radioInput) {
        radioInput.checked = true;
        radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    const productForm = document.querySelector(this.config.productFormSelector);
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.notifyKachingBundle(variantId);

    document.dispatchEvent(new CustomEvent('bigens:variant-changed', {
      detail: { variantId }
    }));

    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: { variant: variantId }
    }));

    const url = new URL(window.location);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  notifyKachingBundle(variantId) {
    const kachingBundle = document.querySelector('kaching-app-bundle');
    if (kachingBundle) {
      if (typeof kachingBundle.updateVariant === 'function') {
        kachingBundle.updateVariant(variantId);
      }
      kachingBundle.dispatchEvent(new CustomEvent('variant-change', {
        detail: { variantId },
        bubbles: true
      }));
    }

    const kachingSelects = document.querySelectorAll('[data-kaching-bundle-select]');
    kachingSelects.forEach(select => {
      const option = select.querySelector(`option[value="${variantId}"]`);
      if (option) {
        select.value = variantId;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    if (window.KachingAppBundle) {
      try {
        if (typeof window.KachingAppBundle.setVariant === 'function') {
          window.KachingAppBundle.setVariant(variantId);
        }
        if (typeof window.KachingAppBundle.updatePrice === 'function') {
          window.KachingAppBundle.updatePrice();
        }
      } catch (e) {
        console.warn('Erro ao atualizar Kaching Bundle:', e);
      }
    }

    document.dispatchEvent(new CustomEvent('kaching:variant-change', {
      detail: { variantId },
      bubbles: true
    }));

    setTimeout(() => {
      const kachingUpdateBtn = document.querySelector('[data-kaching-update], .kaching-update-btn');
      if (kachingUpdateBtn) kachingUpdateBtn.click();
    }, 50);
  }

  observeVariantChanges() {
    const productForm = document.querySelector(this.config.productFormSelector);
    if (!productForm) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const variantId = parseInt(mutation.target.value);
          this.syncStateWithVariant(variantId);
        }
      });
    });

    const variantInput = productForm.querySelector('input[name="id"]');
    if (variantInput) {
      observer.observe(variantInput, { attributes: true });
      variantInput.addEventListener('change', (e) => {
        const variantId = parseInt(e.target.value);
        if (!isNaN(variantId)) this.syncStateWithVariant(variantId);
      });
    }
  }

  syncStateWithVariant(variantId) {
    if (this.state.variants.oneTime && this.state.variants.oneTime.id === variantId) {
      if (this.state.selectedType !== 'one-time') {
        this.state.selectedType = 'one-time';
        this.render();
        this.attachEventListeners();
        this.updateAddToCartButton();
      }
    } else {
      const subVariant = this.state.variants.subscriptions.find(v => v.id === variantId);
      if (subVariant) {
        if (this.state.selectedType !== 'subscription' || this.state.selectedPeriod !== subVariant.period) {
          this.state.selectedType = 'subscription';
          this.state.selectedPeriod = subVariant.period;
          this.render();
          this.attachEventListeners();
          this.updateAddToCartButton();
        }
      }
    }
  }

  refresh() {
    this.loadProductData();
    this.identifyVariants();
    this.render();
    this.attachEventListeners();
  }
}

window.BigensSubscriptionSelectorV2 = BigensSubscriptionSelectorV2;
